import React, { useState, useEffect } from 'react';
import { X, MessageSquare, Calendar, Clock, Star, Award } from 'lucide-react';
import { COLORS, USERS_COLLECTION } from '../../config/constants';
import { doc, getDoc } from 'firebase/firestore';
import { useAppContext } from '../../App.jsx';
import SchedulingModal from './SchedulingModal';

const ServiceRequestModal = ({ chat, user, onClose, onGoToChat, scheduleService }) => {
    const { db } = useAppContext();
    const [isScheduling, setIsScheduling] = useState(false);
    const [clientProfile, setClientProfile] = useState(null);

    const clientParticipantId = chat?.participants?.find(p => p !== user?.uid);
    const clientDetailsFromChat = clientParticipantId ? chat?.participantDetails?.[clientParticipantId] : null;
    const clientName = clientDetailsFromChat?.name || 'Cliente Desconhecido';
    const clientPhoto = clientDetailsFromChat?.photoUrl;

    useEffect(() => {
        const fetchClientData = async () => {
            if (!db || !clientParticipantId) return;
            try {
                const userDocRef = doc(db, USERS_COLLECTION, clientParticipantId);
                const userDoc = await getDoc(userDocRef);
                if (userDoc.exists()) {
                    const userData = userDoc.data();
                    setClientProfile(userData.profile_client || {});
                }
            } catch (error) {
                console.error("Erro ao buscar dados do cliente:", error);
            }
        };
        fetchClientData();
    }, [db, clientParticipantId]);

    if (!chat) return null;

    const hasScheduling = chat.scheduledAt && typeof chat.scheduledAt.toDate === 'function';
    let formattedDateTime = null;
    if (hasScheduling) {
        const date = chat.scheduledAt.toDate();
        formattedDateTime = `${date.toLocaleDateString('pt-BR', {day: '2-digit', month: 'long', year: 'numeric'})} às ${date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
    }

    const handleScheduleSubmit = (scheduledDate) => {
        if(scheduleService) {
            scheduleService(chat.id, scheduledDate);
        }
        setIsScheduling(false);
        onClose();
    };

    const displayRating = clientProfile?.rating !== undefined ? clientProfile.rating : (clientDetailsFromChat?.rating || chat.clientRating);
    const topTag = clientProfile?.topTag || chat.clientTopTag;

    return (
        <>
            <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 transition-all" onClick={onClose}>
                <div
                    className="bg-gray-50 rounded-[2rem] w-full max-w-lg relative animate-slide-up flex flex-col overflow-hidden shadow-2xl"
                    style={{ maxHeight: '90vh' }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Cabeçalho Compacto */}
                    <header className="p-4 bg-white border-b flex justify-between items-center shrink-0">
                        <div>
                            <h2 className="text-lg font-black text-gray-800 leading-tight">Detalhes da Solicitação</h2>
                            <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">ID: {chat.id?.substring(0, 8).toUpperCase()}</p>
                        </div>
                        <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors bg-gray-50 border border-gray-100 active:scale-90">
                            <X size={18} className="text-gray-500" />
                        </button>
                    </header>

                    <main className="p-4 space-y-4 overflow-y-auto">

                        {/* CARD DO CLIENTE COMPACTO COM FOTO SQUIRCLE */}
                        <section className="bg-white p-4 rounded-[1.5rem] shadow-sm border border-gray-100 relative">
                            <div className="flex items-center space-x-3">
                                <div className="relative flex-shrink-0">
                                    {/* CONTAINER SQUIRCLE */}
                                    <div className="w-16 h-16 rounded-2xl overflow-hidden bg-white border-2 border-gray-50 shadow-sm flex items-center justify-center p-0.5">
                                        <img
                                            src={clientPhoto || `https://ui-avatars.com/api/?name=${clientName.replace(/ /g, '+')}&background=E0E0E0&color=757575`}
                                            alt={clientName}
                                            className="w-full h-full object-contain rounded-xl"
                                        />
                                    </div>
                                </div>
                                <div className="flex-grow min-w-0">
                                    <h3 className="text-lg font-black text-gray-900 truncate leading-tight mb-1">{clientName}</h3>

                                    <div className="flex flex-wrap items-center gap-1.5">
                                        {displayRating !== undefined && displayRating !== null && (
                                            <div className="flex items-center bg-yellow-50 px-2 py-0.5 rounded-lg border border-yellow-100">
                                                <Star size={10} className="text-yellow-500 fill-current mr-1" />
                                                <span className="text-[10px] font-black text-yellow-700">
                                                    {(typeof displayRating === 'number' ? displayRating : 0).toFixed(1)}
                                                </span>
                                            </div>
                                        )}
                                        {topTag && (
                                            <div className="flex items-center bg-blue-50 px-2 py-0.5 rounded-lg border border-blue-100">
                                                <Award size={10} className="text-blue-500 mr-1" />
                                                <span className="text-[9px] font-black text-blue-600 uppercase tracking-tighter">
                                                    {topTag}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* BLOCO DO SERVIÇO COMPACTO */}
                        <div className="space-y-1.5 px-1">
                            <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest ml-1 flex items-center">
                                <MessageSquare size={10} className="mr-1.5 text-gray-300" /> Necessidade:
                            </label>
                            <div className="bg-white p-4 rounded-[1.5rem] border-2 border-blue-50 shadow-sm">
                                <p className="text-md font-bold text-gray-800 leading-tight">
                                    {chat.serviceTitle || "Serviço Geral"}
                                </p>
                            </div>
                        </div>

                        {/* STATUS DO AGENDAMENTO COMPACTO */}
                        {hasScheduling ? (
                            <div className="bg-blue-600 p-4 rounded-[1.5rem] shadow-lg shadow-blue-50 relative overflow-hidden">
                                <div className="relative z-10">
                                    <p className="text-[9px] font-black text-blue-100 uppercase tracking-widest flex items-center mb-1">
                                        <Clock className='w-3 h-3 mr-1.5'/>
                                        Agendado
                                    </p>
                                    <p className="text-lg font-black text-white leading-tight">
                                        {formattedDateTime}
                                    </p>
                                </div>
                                <Calendar className="absolute -right-3 -bottom-3 text-white opacity-10" size={80} />
                            </div>
                        ) : (
                            <div className="bg-gray-100 p-4 rounded-[1.5rem] border border-dashed border-gray-300 flex items-center justify-center">
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Sem agendamento definido</p>
                            </div>
                        )}
                    </main>

                    {/* RODAPÉ DE AÇÃO - BOTÕES MAIORES */}
                    <footer className="p-4 bg-white border-t flex flex-col gap-2 shrink-0">
                        <div className="grid grid-cols-2 gap-3">
                            <button
                                onClick={() => setIsScheduling(true)}
                                className="py-4 text-white font-black text-sm rounded-2xl flex items-center justify-center hover:opacity-90 transition shadow-lg active:scale-[0.98] uppercase tracking-widest disabled:opacity-50"
                                style={{ backgroundColor: COLORS.PRIMARY_BLUE }}
                                disabled={!scheduleService}
                            >
                                <Calendar className='w-5 h-5 mr-2' />
                                {hasScheduling ? 'Reagendar' : 'Agendar'}
                            </button>
                            <button
                                onClick={onGoToChat}
                                className="py-4 bg-blue-600 text-white font-black text-sm rounded-2xl flex items-center justify-center hover:bg-blue-700 transition shadow-lg active:scale-[0.98] uppercase tracking-widest"
                            >
                                <MessageSquare className='w-5 h-5 mr-2' />
                                Chat
                            </button>
                        </div>
                    </footer>
                </div>
            </div>

            {isScheduling && <SchedulingModal onClose={() => setIsScheduling(false)} onSubmit={handleScheduleSubmit} />}
        </>
    );
};

export default ServiceRequestModal;
